import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import ChatOverlays from 'components/Utils/ChatOverlays';
import ChatContext from 'contexts/ChatContext';
import { TelemedicineModal } from 'scenes/Telemedicine';
import useChat from 'hooks/useChat';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { Bubble } from 'careand-redux/slices/ui/ui';
import { UserRoleEnum } from 'generated/api/models/User';
import { AnyUser } from 'careand-redux/slices/auth';

type Props = {
  user: AnyUser | undefined;
  children: ReactNode;
};

const ChatController = ({ user, children }: Props) => {
  const dispatch = useAppDispatch();
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const role = useAppSelector((state) => state.authentication.role);
  const video = useAppSelector((state) => state.video);

  const videoBubbleId = video?.bubble?.id;

  const [videoScreen, setVideoScreen] = useState<JSX.Element | null>(null);
  const {
    joinedRooms,
    messages,
    careyActivity,
    sendMessage,
    markMessagesRead,
    unreadMessages,
    leaveRoom,
    unreadCount,
    connected,
    connecting,
    onReconnect,
  } = useChat(activeUserId);

  const userId = user?.id;

  useEffect(() => {
    loadData(userId, role!);
  }, [userId, role]);

  useEffect(() => {
    if ((role === 'physician' || role === 'patient') && video.bubble) {
      setupVideoScreen(video.bubble.patient_id, video.bubble.payload?.room, video.bubble);
    } else {
      setVideoScreen(null);
    }
  }, [video.bubble, videoBubbleId, role]);

  const providerData = useMemo(() => {
    return {
      joinedRooms,
      messages,
      careyActivity,
      sendMessage,
      markMessagesRead,
      unreadMessages,
      leaveRoom,
      unreadCount,
      connected,
      connecting,
      onReconnect,
    };
  }, [
    joinedRooms,
    messages,
    careyActivity,
    sendMessage,
    markMessagesRead,
    unreadMessages,
    leaveRoom,
    unreadCount,
    connected,
    connecting,
    onReconnect,
  ]);

  if (!user) return <>{children}</>;

  return (
    <ChatContext.Provider value={providerData}>
      <ErrorBoundary>{videoScreen}</ErrorBoundary>
      <ErrorBoundary>
        <ChatOverlays onArchive={onArchiveChat} />
      </ErrorBoundary>
      <ErrorBoundary>{children}</ErrorBoundary>
    </ChatContext.Provider>
  );

  function setupVideoScreen(patientId?: string, chatSid?: string, videoBubble?: Bubble) {
    let videoScreen = null;
    if (role === UserRoleEnum.Physician) {
      videoScreen = patientId ? getPhysicianVideo(patientId, videoBubble) : null;
    }
    setVideoScreen(videoScreen);
  }

  function getPhysicianVideo(patientId?: string, videoBubble?: Bubble): JSX.Element {
    return (
      <TelemedicineModal
        id={videoBubble?.appointment_id}
        saveTo={patientId}
        appointmentId={videoBubble?.appointment_id}
      />
    );
  }

  async function loadData(userId: string | undefined, role: string) {
    if (userId) {
      if (role === UserRoleEnum.Patient) {
        dispatch(api.Chat.getUserArchives({ userId, role: UserRoleEnum.Patient }));
      } else if (role === UserRoleEnum.Physician) {
        const videoBubble = video.bubble;
        if (videoBubble !== undefined)
          setupVideoScreen(videoBubble.patient_id, videoBubble.payload?.room, videoBubble);
      }
    }
  }

  async function onArchiveChat(channelSid?: string, appointmentId?: string, roomName?: string) {
    if (roomName) {
      const result = await dispatch(api.Chat.archiveChatRoom({ chatId: roomName }), {
        errorTitle: 'Archive Chat',
      });
      if (result) leaveRoom(roomName);
      return result;
    }
  }
};

export default ChatController;
